import { useForm } from "@inertiajs/react";
import { FormEvent, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Separator } from "../../components/ui/Separator";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../../components/ui/Card";
import SlackLoginPanel from "./components/SlackLoginPanel";

export default function Login({ flash, errors }: { flash?: Record<string, string>; errors?: Record<string, string[]> }) {
  const [mode, setMode] = useState<"password" | "slack">("password");
  const [showPassword, setShowPassword] = useState(false);
  const { data, setData, post, processing } = useForm({
    email: "",
    password: "",
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    post("/login");
  };

  const baseError = errors?.base || flash?.alert;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-muted/40 px-4">
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-[30px] h-[30px] rounded-md bg-foreground text-background flex items-center justify-center text-sm font-semibold">A</div>
        <span className="text-sm font-semibold text-foreground" style={{ letterSpacing: "-0.01em" }}>ATLAS AI</span>
      </div>

      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-xl">Welcome back</CardTitle>
          <CardDescription>Sign in to your account</CardDescription>
        </CardHeader>
        <CardContent>
          {baseError && (
            <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {baseError}
            </div>
          )}

          {mode === "slack" ? (
            <SlackLoginPanel errors={errors} onBack={() => setMode("password")} />
          ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium text-foreground">Email</label>
              <Input
                id="email"
                type="email"
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="text-sm font-medium text-foreground">Password</label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={data.password}
                  onChange={(e) => setData("password", e.target.value)}
                  autoComplete="current-password"
                  className="pr-10"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-9 w-9 text-muted-foreground hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={processing}>
              {processing ? "Signing in…" : "Sign in"}
            </Button>

            <div className="flex items-center gap-3">
              <Separator className="flex-1" />
              <span className="text-xs text-muted-foreground whitespace-nowrap">Or continue with</span>
              <Separator className="flex-1" />
            </div>

            <Button type="button" variant="outline" className="w-full" onClick={() => setMode("slack")}>
              Continue with Slack
            </Button>
          </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
